/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from 'react';
import { css } from '@emotion/react';

import { useTranslation } from 'react-i18next';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';

import IconButton from '@mui/material/IconButton';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { showTemplateDialog } from '../../../utils/dialog';

const VALID_TYPES = ['file', 'folder'];

function noop() {}

const StyledMenuItem = ({ onClick, Icon, label, disabled = false }) => (
  <MenuItem
    onClick={onClick}
    css={css`
      color: var(--text-secondary);
    `}
    disabled={disabled}
  >
    <Icon
      color="inherit"
      css={css`
        margin-right: 0.5rem;
      `}
    />
    {label}
  </MenuItem>
);

export default function MoreMenu({
  folderPrefix,
  onEdit = noop,
  onDelete = noop,
  deletePermission = false,
  download = false,
  showMove = true,
  onShowExport = noop,
  onMove = noop,
  isCompact = false,
  allowMoveBetweenProjects = false,
  disableDownload = false,
  type = 'file',
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();

  const handleMove = useCallback(
    async (e) => {
      e.stopPropagation();
      setAnchorEl(null);
      // folders have a trailing / which needs to be stripped
      const parent = folderPrefix.endsWith('/')
        ? folderPrefix.substring(0, folderPrefix.lastIndexOf('/'))
        : folderPrefix;
      const assets = await showTemplateDialog('assetPicker', {
        folderPrefix: parent,
        types: ['folder'],
        folderSelect: true,
        singleSelect: true,
      });
      if (!assets || Object.keys(assets).length < 1) return;
      const picked = assets[Object.keys(assets)[0]];
      onMove({
        folder_prefix: picked.folderPrefix,
        old_folder_prefix: folderPrefix,
      });
    },
    [folderPrefix, onMove]
  );

  if (!VALID_TYPES.includes(type)) {
    throw new Error('Invalid type provided to MoreMenu');
  }

  if (!deletePermission && !download && !showMove) {
    return null;
  }

  return (
    <div>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }}
        css={css`
          padding: 0;
        `}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={(e) => {
          e.stopPropagation();
          setAnchorEl(null);
        }}
        anchorOrigin={
          isCompact
            ? {
                vertical: 'bottom',
                horizontal: 'left',
              }
            : undefined
        }
        transformOrigin={
          isCompact
            ? {
                vertical: 'top',
                horizontal: 'right',
              }
            : undefined
        }
      >
        <StyledMenuItem
          label={t('Redigera')}
          Icon={EditIcon}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
            setAnchorEl(null);
          }}
        />
        {deletePermission && (
          <StyledMenuItem
            label={t('Ta bort')}
            Icon={DeleteIcon}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              setAnchorEl(null);
            }}
          />
        )}
        {download && (
          <StyledMenuItem
            label={t('Ladda ner')}
            Icon={FileDownloadOutlinedIcon}
            onClick={onShowExport}
            disabled={disableDownload}
          />
        )}
        {deletePermission && showMove ? (
          <StyledMenuItem
            label={type === 'file' ? t(`Flytta fil`) : t('Flytta')}
            Icon={DriveFileMoveOutlinedIcon}
            onClick={handleMove}
          />
        ) : null}
      </Menu>
    </div>
  );
}
